import React, { createRef, useState } from 'react';
import { Paper, Stack, Typography, Button, Box, Skeleton, Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChartContainer, BarPlot, ChartsAxis, ChartsGrid, ChartsTooltip, ChartsReferenceLine } from '@mui/x-charts';
import { useTheme } from '@mui/material/styles';
import VerticalAlignBottomOutlinedIcon from '@mui/icons-material/VerticalAlignBottomOutlined';
import ChartToggleControls from '../common/ChartToggleControls';
import { NEEDisplayMode, ChartType } from '../../types/enums';
import { AnnualNEEData, MonthlyNEEData } from '../../types/schema';
import { toPng, toJpeg } from 'html-to-image';

const ChartPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}));

const ChartHeader = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

interface NEEChartProps {
  title: string;
  chartType: ChartType;
  data: AnnualNEEData[] | MonthlyNEEData[];
  displayMode: NEEDisplayMode;
  onDisplayModeChange: (mode: NEEDisplayMode) => void;
  onDownload?: () => void;
  height?: number;
  loading?: boolean;
  selectedMonths?: number[];
  hasSelectedPolygons?: boolean;
  hasFiltersApplied?: boolean;
}

const NEEChart: React.FC<NEEChartProps> = ({
  title,
  chartType,
  data,
  displayMode,
  onDisplayModeChange,
  height = 400,
  loading = false,
  selectedMonths,
  hasSelectedPolygons = false,
  hasFiltersApplied = false,
}) => {
  const monthsNamelist = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const formatMonths = (months: number[] | undefined) => {
    if (!months || months.length === 0) {
      return null;
    }
    if (months.length === 12) {
      return null; // All months, don't show
    }
    if (months.length === 1) {
      return monthsNamelist[months[0] - 1];
    }
    const sortedMonths = [...months].sort((a, b) => a - b);
    const isContinuous = sortedMonths.every((month, index) => {
      if (index === 0) return true;
      return month === sortedMonths[index - 1] + 1;
    });

    if (isContinuous) {
      const start = monthsNamelist[sortedMonths[0] - 1];
      const end = monthsNamelist[sortedMonths[sortedMonths.length - 1] - 1];
      return `${start} - ${end}`;
    }
    return sortedMonths.map(m => monthsNamelist[m - 1]).join(', ');
  };

  const monthFilterText = chartType === ChartType.ANNUAL ? formatMonths(selectedMonths) : null;
  const theme = useTheme();
  const chartRef = createRef<HTMLDivElement>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadCSV = () => {
    const { xAxisData, seriesData, xAxisLabel, yAxisLabel } = getChartData();
    let csvContent = `"${xAxisLabel}","${yAxisLabel}"\n`;
    xAxisData.forEach((label, index) => {
      csvContent += `"${label}",${seriesData[index]}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-s-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${title.replace(/ /g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  const handleDownloadImage = (format: 'png' | 'jpeg') => {
    if (chartRef.current === null) {
      return;
    }

    const downloader = format === 'png' ? toPng : toJpeg;
    downloader(chartRef.current, {
      cacheBust: true,
      backgroundColor: theme.palette.background.paper,
    })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `${title.replace(/ /g, '_')}.${format}`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error(err);
      });
    handleMenuClose();
  };

  const getChartData = () => {
    if (chartType === ChartType.ANNUAL) {
      const annualData = data as AnnualNEEData[];
      let scale = 1;
      let yAxisLabel = displayMode === NEEDisplayMode.TOTAL ? 'NEE Total (tCO2)' : 'NEE Intensity (tCO2/ha)';

      if (displayMode === NEEDisplayMode.TOTAL) {
        // Check if any value has absolute value >= 1000
        const hasLargeValues = annualData.some(d => Math.abs(d.neeTotal) >= 1000);
        if (hasLargeValues) {
          scale = 1000;
          yAxisLabel = 'NEE Total (ktCO2)';
        }
      }

      return {
        xAxisData: annualData.map(d => d.year),
        seriesData: annualData.map(d =>
          displayMode === NEEDisplayMode.TOTAL ? (d.neeTotal / scale) : d.neeIntensity
        ),
        xAxisLabel: 'Year',
        yAxisLabel,
      };
    } else {
      const monthlyData = data as MonthlyNEEData[];
      let scale = 1;
      let yAxisLabel = displayMode === NEEDisplayMode.TOTAL ? 'NEE Total (tCO2)' : 'NEE Intensity (tCO2/ha)';

      if (displayMode === NEEDisplayMode.TOTAL) {
        // Check if any value has absolute value >= 1000
        const hasLargeValues = monthlyData.some(d => Math.abs(d.neeTotal) >= 1000);
        if (hasLargeValues) {
          scale = 1000;
          yAxisLabel = 'NEE Total (ktCO2)';
        }
      }

      return {
        xAxisData: monthlyData.map(d => d.monthYear),
        seriesData: monthlyData.map(d =>
          displayMode === NEEDisplayMode.TOTAL ? (d.neeTotal / scale) : (d.neeIntensity || 0)
        ),
        xAxisLabel: 'Month',
        yAxisLabel,
      };
    }
  };

  const chartData = getChartData();

  /*
  const getSeriesWithColors = () => {
    return chartData.seriesData.map((value, /!*index*!/) => ({
      data: [value],
      color: value < 0 ? theme.palette.secondary.main : theme.palette.error.main,
    }));
  };
  */

  return (
    <ChartPaper>
      <ChartHeader direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="column" spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {hasFiltersApplied && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              This graph reflects data from months in which all filter criteria are met.
            </Typography>
          )}
          {monthFilterText && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Displaying aggregated data for: {monthFilterText}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <ChartToggleControls
            displayMode={displayMode}
            onDisplayModeChange={onDisplayModeChange}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<VerticalAlignBottomOutlinedIcon />}
            onClick={handleMenuClick}
            sx={{ minWidth: 'auto' }}
          >
            Download
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleDownloadCSV}>Download CSV</MenuItem>
            <MenuItem onClick={() => handleDownloadImage('jpeg')}>Download JPG</MenuItem>
            <MenuItem onClick={() => handleDownloadImage('png')}>Download PNG</MenuItem>
          </Menu>
        </Stack>
      </ChartHeader>

      <Box sx={{ width: '100%', height, position: 'relative' }} ref={chartRef}>
        {loading ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={height * 0.8} animation="wave" />
            <Skeleton variant="text" width="60%" animation="wave" />
            <Skeleton variant="text" width="40%" animation="wave" />
          </Stack>
        ) : (
          <ChartContainer
            xAxis={[
              {
                scaleType: 'band',
                data: chartData.xAxisData,
                label: chartData.xAxisLabel,
                tickPlacement: 'middle',
                tickLabelPlacement: 'middle',
                disableLine: true,
                valueFormatter: (value) => {
                  // Format year without comma separator
                  if (typeof value === 'number') {
                    return value.toString();
                  }
                  return String(value);
                },
              },
            ]}
            yAxis={[
              {
                label: chartData.yAxisLabel,
                valueFormatter: (value) => {
                  if (displayMode === NEEDisplayMode.TOTAL) {
                    return value.toFixed(1);
                  }
                  return value.toFixed(1);
                },
                colorMap: {
                  type: 'piecewise',
                  thresholds: [0],
                  colors: [theme.palette.success.main, theme.palette.error.main],
                },
              },
            ]}
            series={[
              {
                type: 'bar',
                data: chartData.seriesData,
                label: (location) => location === 'tooltip' ? chartData.yAxisLabel : '',
                valueFormatter: (value) => (value !== null ? value.toFixed(2) : ''),
              },
            ]}
            width={undefined}
            height={height}
            margin={{
              left: 60,
              right: 30,
              top: 20,
              bottom: 20,
            }}
            sx={{
              '& .MuiChartsAxis-left .MuiChartsAxis-label': {
                transform: 'translateX(-30px)',
              },
              '& .MuiBarElement-root': {
                clipPath: 'inset(0px round 4px 4px 0px 0px)',
              },
              '& .MuiChartsGrid-line': {
                stroke: 'rgba(150, 150, 150, 0.4)',
                strokeDasharray: '0',
              },
            }}
          >
            <ChartsGrid horizontal />
            <BarPlot />
            <ChartsReferenceLine
              y={0}
              lineStyle={{
                stroke: '#000000',
                strokeWidth: 1
              }}
            />
            <ChartsAxis />
            <ChartsTooltip />
          </ChartContainer>
        )}
      </Box>
    </ChartPaper>
  );
};

export default NEEChart;
